import { CAPTCHA_ACTIONS, InputVariantTypes, User } from "@ecologi/shared";
import styled from "@emotion/styled";
import { Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { Stripe, StripeCardNumberElement } from "@stripe/stripe-js";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { Button } from "../../Button";
import {
  FormLayout,
  RowItem,
  RowItems,
} from "../../FormLayout/FormLayout.styles";
import { InlineBanner } from "../../InlineBanner";
import { RecaptchaPolicy, useRecaptcha } from "../../Recaptcha";
import {
  StripeCardCVC,
  StripeCardExpiry,
  StripeCardNumber,
} from "../../StripeElements";
import { Typography } from "../../Typography";

const StyledFormLayout = styled(FormLayout)`
  margin-bottom: 20px;
`;

const ButtonBlock = styled.div<{
  buttonPlacement: "left" | "right" | "spread";
}>`
  display: flex;
  margin-top: 32px;
  margin-bottom: 20px;
  justify-content: ${({ buttonPlacement }) =>
    buttonPlacement === "right"
      ? "flex-end"
      : buttonPlacement === "left"
      ? "flex-start"
      : "space-between"};
  align-items: center;
`;

const StyledRecaptchaPolicy = styled(RecaptchaPolicy)`
  font-size: 14px;
`;

const LowerContent = styled(Typography)`
  color: ${({ theme }) => theme.colors.charcoalLight};
`;

export type AddCardDetailsArgs = {
  preferredCurrency: string;
  cardElement: StripeCardNumberElement;
  captchaToken: string;
};

type BillingPaymentProps = {
  user: User;
  addCardDetails: (args: AddCardDetailsArgs) => Promise<any>;
  stripe: Stripe | null;
  setHasUpdated: (isUpdated: boolean) => void;
  showWarning?: boolean;
  showTitle?: boolean;
  inputVariant?: InputVariantTypes;
  showCaptcha?: boolean;
  showStripe?: boolean;
  showCancel?: boolean;
  onCancel?: () => void;
  lowerContent?: string;
  buttonPlacement?: "left" | "right" | "spread";
};

const BillingPaymentSetupBase = ({
  addCardDetails,
  user,
  setHasUpdated,
  showCaptcha = true,
  showStripe = true,
  showWarning = true,
  showTitle = true,
  inputVariant = InputVariantTypes.DEFAULT,
  showCancel = false,
  lowerContent,
  onCancel,
  buttonPlacement = "left",
}: BillingPaymentProps) => {
  const [isLoading, setLoadingState] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const recaptcha = useRecaptcha();

  const [isCardNumberInteracted, setCardNumberInteracted] = useState(false);
  const [isCardExpiryInteracted, setCardExpiryInteracted] = useState(false);
  const [isCardCVCInteracted, setCardCVCInteracted] = useState(false);

  const title = user.giftingUserType
    ? "As you received a gift, you don't have any billing details saved. If you'd like to join our monthly climate action subscription add your details below."
    : "You don't have any billing details saved yet. Please add your payment details below.";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // It automatically pulls the other elements from the same form
    const cardElement = elements ? elements.getElement("cardNumber") : null;

    if (!cardElement) {
      toast.error(
        "An error has occurred with our payment provider. Please refresh and try again."
      );
      return;
    }

    setLoadingState(true);

    const captchaToken = await recaptcha.execute(
      CAPTCHA_ACTIONS.SUBSCRIPTION_SETUP_INTENT
    );
    await addCardDetails({
      preferredCurrency: user.preferredCurrency,
      cardElement,
      captchaToken,
    });

    setLoadingState(false);
    setHasUpdated(true);
  };

  const isButtonDisabled =
    !isCardNumberInteracted || !isCardExpiryInteracted || !isCardCVCInteracted;

  return (
    <>
      {showWarning && (
        <InlineBanner title={title} status="warning">
          {user.subscription?.isFailingPayment && (
            <p>
              Your card will immediately be charged with this month's
              subscription amount.
            </p>
          )}
        </InlineBanner>
      )}
      {showTitle && (
        <Typography use="componentTitle">Add your payment details</Typography>
      )}

      <form onSubmit={handleSubmit}>
        <StyledFormLayout>
          <RowItem>
            <StripeCardNumber
              isDisabled={isLoading}
              label="Enter your new card number"
              helpText="Enter your 16-digit card number"
              variant={inputVariant}
              onChange={() => setCardNumberInteracted(true)}
            />
          </RowItem>
          <RowItems>
            <RowItem>
              <StripeCardExpiry
                isDisabled={isLoading}
                label="Expiry date"
                helpText="Enter your card’s expiry date"
                variant={inputVariant}
                onChange={() => setCardExpiryInteracted(true)}
              />
            </RowItem>
            <RowItem>
              <StripeCardCVC
                isDisabled={isLoading}
                label="CVC"
                helpText="Enter your three digit CVC number"
                variant={inputVariant}
                onChange={() => setCardCVCInteracted(true)}
              />
            </RowItem>
          </RowItems>
        </StyledFormLayout>
        {lowerContent && (
          <LowerContent use="body2">{lowerContent}</LowerContent>
        )}
        <ButtonBlock
          buttonPlacement={
            showCancel || showStripe ? "spread" : buttonPlacement
          }
        >
          {showCancel && (
            <Button text="Cancel" textOnlyWithBorder onClick={onCancel} />
          )}

          <Button
            disabled={isLoading || isButtonDisabled}
            darkGreen
            loading={isLoading}
            submit
            text="Save"
            data-testid="save-payment-details-button"
          />

          {showStripe && (
            <a
              href="https://stripe.com"
              rel="noopener noreferrer"
              target="_blank"
              title="View the Stripe website (new tab)"
            >
              <img
                src="/images/app/stripe/powered-by-stripe-black.svg"
                alt="Powered by Stripe"
                width="96"
                height="21"
              />
            </a>
          )}
        </ButtonBlock>
        {showCaptcha && <StyledRecaptchaPolicy />}
      </form>
    </>
  );
};

export const BillingPaymentSetup = (props: BillingPaymentProps) => {
  const { stripe } = props;
  return (
    <Elements stripe={stripe}>
      <BillingPaymentSetupBase {...props} />
    </Elements>
  );
};
